/**
 * Übergabe an ERP und Steuerberater — `EXP-001`, `04` §4.13, `07` §7.16.
 *
 * **Das ist keine Nebenfunktion.** `CLAUDE.md` nennt das Zielbild: „Ein Betrieb
 * soll am Ende neben der Plattform höchstens noch ein ERP brauchen. Angebot,
 * Kalkulation, Ausgangsrechnung, Buchhaltung und Bestandsführung werden
 * bewusst nicht gebaut — die **Übergabe** dorthin und zum Steuerberater gehört
 * dagegen zum Produkt."
 *
 * Daraus folgt die Anforderung, die schon die Monteur-App prägt: Zeiten und
 * Material werden **abrechnungsfähig** erfasst, nicht als freie Notiz. Hier
 * zeigt sich, wofür.
 *
 * **Zwei Empfänger, zwei Zuschnitte.**
 *
 *   ERP             Was abgerechnet wird: Auftragszeiten und Material je
 *                   Einsatz, mit Kundenbezug und externer Kundennummer, wo es
 *                   eine gibt. Kein Bericht, keine Notiz, kein Chat.
 *   Steuerberater   Was belegt wird: Eingangsrechnungen mit Hash und
 *                   Validierungsprotokoll (`04` §4.13). Keine Aufträge, keine
 *                   Personaldaten — die Rolle `external-partner` hat nach
 *                   `07` §7.2 darauf ohnehin keinen Zugriff.
 *
 * Ein gemeinsamer Export wäre einfacher zu bauen und falsch: Er gäbe beiden
 * Empfängern, was nur einer braucht.
 *
 * **CSV, nicht ein eigenes Format.** `04` §4.13 verlangt „offenes JSON/CSV";
 * jede Branchensoftware liest CSV, und ein eigenes Format wäre die Sorte
 * Bindung, gegen die dieses ganze Vorhaben antritt.
 */

import { useMemo, useState } from "react";

import type { Bestand } from "@hwc/demo-data";
import {
  dauerMinuten,
  findeAuftrag,
  findeEinsatz,
  findeKunde,
  findePerson,
} from "@hwc/demo-data";
import { formatResourceRef } from "@hwc/contracts";
import { Knopf } from "@hwc/ui";

import { useSitzung } from "../../src/daten/Sitzung.js";
import { Feld, Felder, Liste, Zeile } from "../../src/bereiche/bausteine.js";

/**
 * Eine CSV-Zeile.
 *
 * Semikolon als Trenner, nicht Komma: Deutsche Tabellenkalkulationen erwarten
 * es so, und ein Betrag wie `1.284,55` enthält selbst ein Komma. Wer hier das
 * Falsche wählt, liefert eine Datei, die beim Öffnen in eine Spalte fällt —
 * und das merkt der Empfänger, nicht der Absender.
 */
function csvZeile(felder: readonly (string | number)[]): string {
  return felder
    .map((f) => {
      const text = String(f);
      // Anführungszeichen verdoppeln und einpacken, wo es nötig ist. Ein
      // Auftragstitel enthält regelmäßig ein Semikolon.
      return /[";\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
    })
    .join(";");
}

/**
 * Die Leistungen, die ins ERP gehen.
 *
 * **Stornierte Einsätze sind dabei — und markiert.** ADR-045 nimmt in Kauf,
 * dass ein storniertes Einsatzobjekt bereits gebuchte Zeiten trägt, und
 * `09` §9.3 führt als offene Frage an `EXP-001`, was bei der Übergabe damit
 * geschieht. Sie wegzulassen wäre eine Antwort; sie unmarkiert mitzuschicken
 * auch. Beides wäre eine Entscheidung im Vorbeigehen, deshalb stehen sie mit
 * einer eigenen Spalte da — der Empfänger entscheidet, und die Frage bleibt
 * sichtbar offen.
 */
function leistungenCsv(bestand: Bestand): string {
  const kopf = csvZeile([
    "Auftragsnummer",
    "Auftragstitel",
    "Kundennummer",
    "ExterneKundennummer",
    "Kunde",
    "Einsatz",
    "Datum",
    "Art",
    "Monteur",
    "Minuten",
    "Abrechenbar",
    "Einsatzstorniert",
  ]);

  const zeilen: string[] = [];

  for (const zeit of bestand.zeiten) {
    const einsatz = findeEinsatz(bestand, zeit.einsatz);
    if (!einsatz) continue;
    const auftrag = findeAuftrag(bestand, einsatz.auftrag);
    const kunde = auftrag ? findeKunde(bestand, auftrag.kunde) : undefined;

    zeilen.push(
      csvZeile([
        auftrag?.nummer ?? "",
        auftrag?.titel ?? "",
        kunde?.nummer ?? "",
        kunde?.externeKundennummer ?? "",
        kunde?.name ?? "",
        einsatz.titel,
        zeit.von.slice(0, 10),
        zeit.art,
        findePerson(bestand, zeit.monteur)?.name ?? "",
        dauerMinuten(zeit.von, zeit.bis),
        zeit.abrechenbar ? "ja" : "nein",
        einsatz.zustand === "storniert" ? "ja" : "nein",
      ]),
    );
  }

  return [kopf, ...zeilen].join("\n");
}

function materialCsv(bestand: Bestand): string {
  const kopf = csvZeile([
    "Auftragsnummer",
    "Kundennummer",
    "ExterneKundennummer",
    "Einsatz",
    "Datum",
    "Bezeichnung",
    "Artikelnummer",
    "Menge",
    "Einheit",
    "Abrechenbar",
    "Einsatzstorniert",
  ]);

  const zeilen = bestand.material.map((m) => {
    const einsatz = findeEinsatz(bestand, m.einsatz);
    const auftrag = einsatz ? findeAuftrag(bestand, einsatz.auftrag) : undefined;
    const kunde = auftrag ? findeKunde(bestand, auftrag.kunde) : undefined;

    return csvZeile([
      auftrag?.nummer ?? "",
      kunde?.nummer ?? "",
      kunde?.externeKundennummer ?? "",
      einsatz?.titel ?? "",
      m.erfasstAm.slice(0, 10),
      m.bezeichnung,
      m.artikelnummer ?? "",
      m.menge,
      m.einheit,
      m.abrechenbar ? "ja" : "nein",
      einsatz?.zustand === "storniert" ? "ja" : "nein",
    ]);
  });

  return [kopf, ...zeilen].join("\n");
}

/**
 * Die Belege für den Steuerberater.
 *
 * `04` §4.13 verlangt „E-Rechnungsoriginale mit Hash, Validierungsprotokoll und
 * Verknüpfung zur Eingangsnachricht". Alle drei stehen in der Datei — der Hash
 * ist der Teil, den man weglassen möchte, weil er unleserlich ist, und der
 * einzige, der die Unverändertheit belegt.
 */
function belegeCsv(bestand: Bestand): string {
  const kopf = csvZeile([
    "Rechnungssteller",
    "Rechnungsnummer",
    "Datum",
    "BetragCent",
    "Waehrung",
    "Profil",
    "Validierung",
    "Befunde",
    "OriginalHash",
    "Eingangsvorgang",
    "ZugeordneterAuftrag",
  ]);

  const zeilen = bestand.rechnungen.map((r) => {
    const vorgang = bestand.eingang.find(
      (v) => formatResourceRef(v.ref) === formatResourceRef(r.eingangsvorgang),
    );
    const auftrag = findeAuftrag(bestand, vorgang?.zugeordneterAuftrag);

    return csvZeile([
      r.steller,
      r.rechnungsnummer,
      r.datum,
      r.betragCent,
      r.waehrung,
      r.profil,
      r.validierung,
      // Vollständig, nicht gekürzt: Der Steuerberater sieht sonst „gültig"
      // und erfährt nichts von der Anmerkung dahinter.
      r.befunde.join(" | "),
      r.originalHash,
      formatResourceRef(r.eingangsvorgang),
      auftrag?.nummer ?? "",
    ]);
  });

  return [kopf, ...zeilen].join("\n");
}

/**
 * Der Link-Graph als JSONL — `04` §4.13.
 *
 * Eine Zeile je Kante, weil ein Graph mit zehntausend Kanten als eine
 * JSON-Datei nicht mehr zeilenweise verarbeitbar ist. Das Format steht
 * ausdrücklich im Dokument.
 */
function graphJsonl(bestand: Bestand): string {
  return bestand.kanten
    .map((k) =>
      JSON.stringify({
        source: formatResourceRef(k.source),
        relation: k.relation,
        target: formatResourceRef(k.target),
        createdBy: formatResourceRef(k.createdBy),
        createdAt: k.createdAt,
        ...(k.deletedAt ? { deletedAt: k.deletedAt } : {}),
      }),
    )
    .join("\n");
}

export function Uebergabe() {
  const { bestand } = useSitzung();
  const [vorschau, setzeVorschau] = useState<{ name: string; inhalt: string } | null>(null);

  const zahlen = useMemo(() => {
    const abrechenbar = bestand.zeiten.filter((z) => z.abrechenbar);
    const minuten = abrechenbar.reduce((s, z) => s + dauerMinuten(z.von, z.bis), 0);
    const storniert = bestand.zeiten.filter((z) => {
      const e = findeEinsatz(bestand, z.einsatz);
      return e?.zustand === "storniert";
    });
    return {
      zeitposten: bestand.zeiten.length,
      abrechenbareStunden: Math.round(minuten / 6) / 10,
      material: bestand.material.length,
      belege: bestand.rechnungen.length,
      kanten: bestand.kanten.length,
      aufStornierten: storniert.length,
    };
  }, [bestand]);

  const pakete = [
    {
      name: "leistungen.csv",
      titel: "Leistungen für das ERP",
      was: "Auftragszeiten je Einsatz mit Kunden- und Auftragsbezug",
      bauen: () => leistungenCsv(bestand),
    },
    {
      name: "material.csv",
      titel: "Material für das ERP",
      was: "Materialposten mit Menge, Einheit und Artikelnummer",
      bauen: () => materialCsv(bestand),
    },
    {
      name: "belege.csv",
      titel: "Belege für den Steuerberater",
      was: "E-Rechnungen mit Hash, Validierungsbefund und Eingangsbezug",
      bauen: () => belegeCsv(bestand),
    },
    {
      name: "linkgraph.jsonl",
      titel: "Link-Graph",
      was: "eine Zeile je Beziehung — für einen Umzug, nicht für die Buchhaltung",
      bauen: () => graphJsonl(bestand),
    },
  ];

  return (
    <>
      <h1>Übergabe</h1>
      <p>
        An die Branchensoftware und an den Steuerberater. <strong>Zwei Empfänger, zwei
        Zuschnitte</strong> — ein gemeinsamer Export gäbe beiden, was nur einer braucht.
      </p>

      <Felder>
        <Feld name="Zeitposten">{zahlen.zeitposten}</Feld>
        <Feld name="Abrechenbar">{zahlen.abrechenbareStunden} h</Feld>
        <Feld name="Materialposten">{zahlen.material}</Feld>
        <Feld name="Belege">{zahlen.belege}</Feld>
        <Feld name="Kanten im Graph">{zahlen.kanten}</Feld>
      </Felder>

      {zahlen.aufStornierten > 0 && (
        <p className="notiz notiz--warnung">
          {/*
            Die offene Frage aus `09` §9.3, an der Stelle, an der sie
            beantwortet werden müsste — sichtbar, statt im Vorbeigehen
            entschieden.
          */}
          <strong>
            {zahlen.aufStornierten}{" "}
            {zahlen.aufStornierten === 1 ? "Zeitposten hängt" : "Zeitposten hängen"} an
            stornierten Einsätzen.
          </strong>{" "}
          ADR-045 nimmt das in Kauf; was bei der Übergabe mit ihnen geschieht, ist nach{" "}
          <code>09</code> §9.3 <strong>nicht entschieden</strong>. Sie stehen deshalb mit
          einer eigenen Spalte in der Datei — weder weggelassen noch stillschweigend
          mitgezählt.
        </p>
      )}

      <h2>Pakete</h2>
      <Liste>
        {pakete.map((p) => (
          <Zeile
            key={p.name}
            titel={p.titel}
            unterzeile={
              <>
                {p.name} · {p.was}
              </>
            }
            rechts={
              <Knopf
                type="button"
                art="neben"
                onClick={() => setzeVorschau({ name: p.name, inhalt: p.bauen() })}
              >
                Ansehen
              </Knopf>
            }
          />
        ))}
      </Liste>

      {vorschau && (
        <section className="karte">
          <div className="bereichskopf">
            <h2 style={{ marginTop: 0 }}>{vorschau.name}</h2>
            <span className="bereichskopf__zusatz">
              {vorschau.inhalt.split("\n").length} Zeilen
            </span>
          </div>
          {/*
            Die ersten Zeilen, nicht alles. Wer prüfen will, ob die Spalten
            stimmen, braucht den Kopf und drei Datenzeilen — und ein
            Vorschaufenster mit zweitausend Zeilen prüft niemand.
          */}
          <pre className="vorschau">
            {vorschau.inhalt.split("\n").slice(0, 8).join("\n")}
            {vorschau.inhalt.split("\n").length > 8 && "\n…"}
          </pre>
          <p className="notiz">
            Semikolon als Trenner: Deutsche Tabellenkalkulationen erwarten es so, und ein
            Betrag wie 1.284,55 enthält selbst ein Komma. Wer hier das Falsche wählt,
            liefert eine Datei, die beim Öffnen in eine Spalte fällt — und das merkt der
            Empfänger, nicht der Absender.
          </p>
        </section>
      )}

      <h2>Was hier fehlt</h2>
      <p className="notiz">
        <code>04</code> §4.13 zählt zwölf Exporte auf. Vier davon entstehen hier, weil
        das Portal ihre Daten führt. Die übrigen kommen aus den Quellsystemen —{" "}
        <strong>Dateien über WebDAV, Kalender als ICS, Kontakte als VCF, Mail als
        Mailboxformat, Paperless mit Archivstruktur</strong> — und brauchen ihren
        Adapter. <code>EXP-001</code> in Phase 7 fügt sie zu einem Paket zusammen und
        legt es ab; ein Herunterladen aus dem Browser wäre für einen Bestand dieser
        Größe ohnehin der falsche Weg.
      </p>

      <p className="notiz">
        <strong>Kein proprietäres Format und keine Embeddings als alleinige
        Wissensquelle</strong> (<code>04</code> §4.13). Der Sinn dieser Ansicht ist, dass
        ein Betrieb gehen kann — sonst wäre die Datenhoheit aus <code>01</code> §1.3 ein
        Wort ohne Deckung.
      </p>
    </>
  );
}
